import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t } from "@/i18n";
import ChatModal from "@/components/ChatModal";
import IntermediationRow from "@/components/intermediation/IntermediationRow";
import IntermediationSignDialog from "@/components/intermediation/IntermediationSignDialog";
import { logWarn } from "@/lib/logger";

/** Buyer signs pending intermediation contracts with OTP. */
export default function IntermediationInbox() {
  const { lang, user } = useApp();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [signature, setSignature] = useState(null);
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [chatContract, setChatContract] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/matches/intermediation");
      setItems(data);
    } catch (e) { logWarn("Intermediation inbox load failed", e); }
  };
  useEffect(() => { load(); }, []);

  const openContract = (c) => {
    setActive(c);
    setSignature(null);
    setOtpRequested(false);
    setOtpCode("");
  };

  const closeDialog = () => {
    setActive(null);
    setOtpRequested(false);
    setOtpCode("");
    setSignature(null);
  };

  const requestOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/otp/request", { purpose: "intermediation" });
      setOtpRequested(true);
      toast.success(`Email → ${data.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OTP error");
    } finally {
      setBusy(false);
    }
  };

  const sign = async () => {
    if (!active) return;
    setBusy(true);
    try {
      await api.post(`/matches/intermediation/${active.id}/buyer-sign`, {
        otp_code: otpCode,
        signature_image_base64: signature,
      });
      toast.success(lang === "it" ? "Contratto firmato" : "Contract signed");
      closeDialog();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const download = async (c) => {
    const resp = await api.get(`/matches/intermediation/${c.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `intermediazione-${c.id.slice(0, 8)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const pendingBuyerCount = items.filter(
    (c) => c.buyer_user_id === user.user_id && !c.buyer_signed
  ).length;

  return (
    <Card className="bg-[#FFFAF0] border-[#D8CFBB] p-6" data-testid="intermediation-inbox">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C5A880] mb-1">Aaron &amp; Partners</p>
          <h3 className="text-2xl font-light text-[#1A1610]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            {t("interm_title", lang)}
          </h3>
        </div>
        {pendingBuyerCount > 0 && (
          <Badge className="bg-red-700 text-white text-xs" data-testid="pending-signatures-count">
            {pendingBuyerCount} {t("interm_to_sign", lang)}
          </Badge>
        )}
      </div>

      {!items.length && (
        <p className="text-sm text-[#9A8B72] py-8 text-center">{t("interm_none", lang)}</p>
      )}
      <div className="space-y-3">
        {items.map((c) => (
          <IntermediationRow
            key={c.id}
            contract={c}
            isBuyer={c.buyer_user_id === user.user_id}
            onDownload={download}
            onOpenChat={setChatContract}
            onSign={openContract}
          />
        ))}
      </div>

      <IntermediationSignDialog
        contract={active}
        open={!!active}
        onClose={closeDialog}
        onRequestOtp={requestOtp}
        onSubmit={sign}
        otpRequested={otpRequested}
        otpCode={otpCode}
        setOtpCode={setOtpCode}
        setSignature={setSignature}
        busy={busy}
      />
      <ChatModal
        contract={chatContract}
        open={!!chatContract}
        onOpenChange={(v) => !v && setChatContract(null)}
      />
    </Card>
  );
}
